import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import {
  PencilSquareIcon,
  EyeIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const EmailTemplates = () => {
  const { token, demoMode } = useContext(AppContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlBody: '',
    textBody: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/email/templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (err) {
      setError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      htmlBody: template.htmlBody,
      textBody: template.textBody || '',
      description: template.description || ''
    });
    setEditing(true);
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/email/templates/${selectedTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Template updated successfully');
        await loadTemplates();
        setTimeout(() => {
          setEditing(false);
          setSelectedTemplate(null);
        }, 1500);
      } else {
        setError(data.error || 'Failed to update template');
      }
    } catch (err) {
      setError('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setSelectedTemplate(null);
    setError('');
    setSuccess('');
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (editing && selectedTemplate) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back to Templates
          </button>
          <span className="text-xs text-gray-500">
            {selectedTemplate.isSystem && '(System Template - Cannot Delete)'}
          </span>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Edit Template: {selectedTemplate.name}
          </h3>

          {selectedTemplate.availableVariables && selectedTemplate.availableVariables.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-900 mb-2">Available Variables:</p>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate.availableVariables.map((variable) => (
                  <code
                    key={variable}
                    className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs cursor-pointer hover:bg-blue-200"
                    onClick={() => {
                      navigator.clipboard.writeText(`{{${variable}}}`);
                    }}
                    title="Click to copy"
                  >
                    {`{{${variable}}}`}
                  </code>
                ))}
              </div>
              <p className="text-xs text-blue-700 mt-2">Click a variable to copy it to clipboard</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject Line
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Use {{variables}} for dynamic content"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="When is this template used?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HTML Body
              </label>
              <textarea
                value={formData.htmlBody}
                onChange={(e) => setFormData({ ...formData, htmlBody: e.target.value })}
                rows={15}
                className="w-full font-mono text-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="HTML email body..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Supports HTML and template variables like {'{{siteName}}'}, {'{{recipientName}}'}, etc.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plain Text Body (Optional)
              </label>
              <textarea
                value={formData.textBody}
                onChange={(e) => setFormData({ ...formData, textBody: e.target.value })}
                rows={8}
                className="w-full font-mono text-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Plain text fallback for email clients that don't support HTML..."
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={loading}
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Demo Mode Warning */}
      {demoMode?.enabled && (
        <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">
                Email Disabled in Demo Mode
              </p>
              <p className="mt-1 text-sm text-yellow-700">
                Email sending is forcibly disabled while demo mode is active.
                You can still edit templates but no emails will be sent.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Email Templates:</strong> Customize the emails sent to users and contacts. Click on a template to edit its content.
          System templates cannot be deleted but can be customized to match your branding.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
        {templates.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No Templates</h3>
            <p className="mt-1 text-sm text-gray-500">
              Email templates will appear here once email service is configured.
            </p>
          </div>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className="p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-semibold text-gray-900">
                      {template.name}
                    </h4>
                    {template.isSystem && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        System
                      </span>
                    )}
                    {!template.isActive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {template.description || 'No description provided'}
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    <strong>Subject:</strong> {template.subject}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    <strong>Key:</strong> <code className="bg-gray-100 px-1 py-0.5 rounded">{template.templateKey}</code>
                  </p>
                  {template.availableVariables && template.availableVariables.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">
                        <strong>Variables:</strong>
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {template.availableVariables.slice(0, 5).map((variable) => (
                          <code key={variable} className="text-xs bg-gray-100 px-1 py-0.5 rounded text-gray-700">
                            {`{{${variable}}}`}
                          </code>
                        ))}
                        {template.availableVariables.length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{template.availableVariables.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleEditTemplate(template)}
                  className="ml-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EmailTemplates;
